/**
 * snapshotCodec — the exact editor-state snapshot embedded in each publish
 * commit at `.editor/snapshot.json`, enabling a read-only **preview** of a
 * past version. The render JSON (site.json / pages/*.json from
 * serializeSiteDoc) is lossy and one-way, so we store the editor's own
 * state alongside it.
 *
 * The snapshot is the SAME logical input serializeSiteDoc consumes: the raw
 * xyflow nodes/edges (read from the Y.Doc maps) PLUS the full per-grid
 * GridState (config + items) from the global grid store. Grid *config* is
 * NOT persisted in the Y.Doc (it's re-applied from PAGE_GRID_CONFIG on
 * mount), so a `Y.encodeStateAsUpdate(ydoc)` alone would not be exact —
 * hence a plain-JSON snapshot of nodes + edges + grids.
 *
 * Pure + dependency-free (no yjs, no @fredericrous/grid) so the path
 * constant can be imported by the git read worker without pulling those
 * deps into the worker bundle, and the codec is trivially unit-testable.
 * Plain JSON also sidesteps any base64/btoa byte-limit concern.
 */

export const SNAPSHOT_PATH = ".editor/snapshot.json";
export const SNAPSHOT_VERSION = 1;

export interface EditorSnapshot {
  /** raw xyflow node objects (Array.from(ydoc.getMap("nodes").values())). */
  readonly nodes: ReadonlyArray<unknown>;
  /** raw xyflow edge objects. */
  readonly edges: ReadonlyArray<unknown>;
  /** gridId → GridState (config + items), from useGridStore.getState().grids. */
  readonly grids: Record<string, unknown>;
}

export function serializeSnapshot(s: EditorSnapshot): string {
  return JSON.stringify({
    v: SNAPSHOT_VERSION,
    nodes: s.nodes,
    edges: s.edges,
    grids: s.grids,
  });
}

/** Tolerant parse: bad JSON, a version mismatch (older/newer), or a
 *  shape mismatch all return null so the caller can show "preview not
 *  available" rather than throwing. */
export function parseSnapshot(text: string): EditorSnapshot | null {
  let o: unknown;
  try {
    o = JSON.parse(text);
  } catch {
    return null;
  }
  if (!o || typeof o !== "object") return null;
  const r = o as {
    v?: unknown;
    nodes?: unknown;
    edges?: unknown;
    grids?: unknown;
  };
  if (r.v !== SNAPSHOT_VERSION) return null;
  if (
    !Array.isArray(r.nodes) ||
    !Array.isArray(r.edges) ||
    !r.grids ||
    typeof r.grids !== "object"
  )
    return null;
  return {
    nodes: r.nodes,
    edges: r.edges,
    grids: r.grids as Record<string, unknown>,
  };
}
